import { isFunction, isObject } from 'util';

import React, { ReactElement } from 'react';

import { StatusBeneficiario, ToastServiceStipulator } from '@philips-emr/hpms-stipulator-commons';
import { Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import { RequestResponse } from '@philips-emr/tws-core/lib/services/http-service';
import { isEmptyObject } from '@philips-emr/tws-utils';

const OptionKey = {
  DETAILS: 'details',
  RELEASE: 'release',
  EXCLUDE: 'exclude',
  INCONSISTENCIES: 'inconsistencies',
  PRINT: 'print',
};

enum LoteSituacao {
  AGUARDANDO_ACAO_DO_USUARIO = 'A',
  COM_INCONSISTENCIAS = 'I',
  INTEGRADO_A_PROPOSTA = 'IP',
  LIBERADO = 'L',
  PENDENTE = 'P',
  REJEITADA_PELA_OPERADORA = 'RO',
}

interface LoteData {
  dtInclusao: string;
  dtContratacao: string;
  dtLiberacaoLoteInclusao: string;
  dsSituacao: string;
  dsMotivoRejeicao: string;
  ieSituacao: LoteSituacao;
  nrSeqLote: number;
}

interface BeneficiarioLoteData {
  nrSeqInclusao: number;
  nmPessoaFisica: string;
  dtSolicitacao: string;
  dtLiberacaoSolicInclusao: string;
  dsTipoOperacao: string;
  ieStatus: StatusBeneficiario;
  dsStatus: LoteSituacao;
  dsMotivoReprovacao: string;
  ieInconsistencia: string;
  ieSituacao: LoteSituacao;
  ieTipoOperacao: string,
}

function getBeneficiarioLoteIconColorByStatus(ieStatus: StatusBeneficiario): Color {
  const color = {
    [StatusBeneficiario.B]: Color.SAFETY_GREEN,
    [StatusBeneficiario.P]: Color.SAFETY_NEUTRAL,
    [StatusBeneficiario.AL]: Color.SAFETY_YELLOW,
    [StatusBeneficiario.A]: Color.SAFETY_GREEN,
    [StatusBeneficiario.R]: Color.SAFETY_NEUTRAL,
    [StatusBeneficiario.RO]: Color.SAFETY_RED,
    [StatusBeneficiario.C]: Color.SAFETY_BLUE,
    [StatusBeneficiario.I]: Color.SAFETY_ORANGE,
    [StatusBeneficiario.PENDENTE]: Color.SAFETY_NEUTRAL,
    [StatusBeneficiario.CONFIRMADA]: Color.SAFETY_GREEN,
    [StatusBeneficiario.REJEITADA]: Color.SAFETY_RED,
  };

  return color[ieStatus];
}

function getBeneficiarioLoteIconTypeByStatus(ieStatus: StatusBeneficiario): StampIcon {
  const icon = {
    [StatusBeneficiario.B]: StampIcon.CHECK,
    [StatusBeneficiario.P]: StampIcon.CLOCK,
    [StatusBeneficiario.AL]: StampIcon.WARNING,
    [StatusBeneficiario.A]: StampIcon.CHECK,
    [StatusBeneficiario.R]: StampIcon.CANCELED,
    [StatusBeneficiario.RO]: StampIcon.CANCELED,
    [StatusBeneficiario.C]: StampIcon.CHECK,
    [StatusBeneficiario.I]: StampIcon.WARNING,
    [StatusBeneficiario.PENDENTE]: StampIcon.CLOCK,
    [StatusBeneficiario.CONFIRMADA]: StampIcon.CHECK,
    [StatusBeneficiario.REJEITADA]: StampIcon.CANCELED,
  };

  return icon[ieStatus];
}

function getBeneficiarioLoteStampByStatus(ieStatus: StatusBeneficiario, dsStatus: string): ReactElement {
  const className = `u-beneficiarios-lote__stamp-status-${ieStatus}`;
  return (
    <Stamp
      className={className}
      color={getBeneficiarioLoteIconColorByStatus(ieStatus)}
      icon={getBeneficiarioLoteIconTypeByStatus(ieStatus)}
      label={dsStatus}
    />
  );
}

function getLoteIconColorBySituacao(ieSituacao: LoteSituacao): Color {
  const color = {
    [LoteSituacao.AGUARDANDO_ACAO_DO_USUARIO]: Color.SAFETY_NEUTRAL,
    [LoteSituacao.COM_INCONSISTENCIAS]: Color.SAFETY_ORANGE,
    [LoteSituacao.INTEGRADO_A_PROPOSTA]: Color.SAFETY_GREEN,
    [LoteSituacao.LIBERADO]: Color.SAFETY_BLUE,
    [LoteSituacao.PENDENTE]: Color.SAFETY_YELLOW,
    [LoteSituacao.REJEITADA_PELA_OPERADORA]: Color.SAFETY_RED,
  };

  return color[ieSituacao];
}

function getLoteIconTypeBySituacao(ieSituacao: LoteSituacao): StampIcon {
  const icon = {
    [LoteSituacao.AGUARDANDO_ACAO_DO_USUARIO]: StampIcon.CLOCK,
    [LoteSituacao.COM_INCONSISTENCIAS]: StampIcon.WARNING,
    [LoteSituacao.INTEGRADO_A_PROPOSTA]: StampIcon.CHECK,
    [LoteSituacao.LIBERADO]: StampIcon.CHECK,
    [LoteSituacao.PENDENTE]: StampIcon.WARNING,
    [LoteSituacao.REJEITADA_PELA_OPERADORA]: StampIcon.CANCELED,
  };

  return icon[ieSituacao];
}

function getLoteStampBySituacao(ieSituacao: LoteSituacao, dsSituacao: string): ReactElement {
  return (
    <Stamp icon={getLoteIconTypeBySituacao(ieSituacao)} color={getLoteIconColorBySituacao(ieSituacao)} label={dsSituacao} />
  );
}

function onFailedAction(response: RequestResponse<any>, postAction: () => void): void {
  if (!isObject(response) || isEmptyObject(response)) {
    return;
  }

  ToastServiceStipulator.error(String(Object.values(response.body)[0]));

  if (isFunction(postAction)) {
    postAction();
  }
}

export type {
  LoteData,
  BeneficiarioLoteData,
};

export {
  StatusBeneficiario,
  getBeneficiarioLoteIconColorByStatus,
  getBeneficiarioLoteIconTypeByStatus,
  getBeneficiarioLoteStampByStatus,
  getLoteIconColorBySituacao,
  getLoteIconTypeBySituacao,
  getLoteStampBySituacao,
  LoteSituacao,
  onFailedAction,
  OptionKey,
};
